import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { authFetch } from '../../../utils/authFetch';

interface WorkspaceDetails {
  id: string;
  name: string;
  type: 'personal' | 'team' | 'enterprise';
  structure: 'personal' | 'team' | 'enterprise';
  purpose: 'personal' | 'business' | 'client';
  status: 'active' | 'closed';
  avatarUrl: string | null;
  onboardingVersion: number;
  createdAt: string;
}

const TYPE_LABELS: Record<string, string> = {
  personal: 'Personal',
  team: 'Team',
  enterprise: 'Enterprise',
};

const PURPOSE_LABELS: Record<string, string> = {
  personal: 'Personal Life',
  business: 'Business',
  client: 'Client Services',
};

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

interface GeneralTabProps {
  workspaceId: string;
  authToken: string;
}

/** One row of `GET /api/workspaces/:id/members` — only the fields the transfer picker reads. */
interface WorkspaceMemberRow {
  userId: string;
  email?: string | null;
  name?: string | null;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  status: string;
}

/** The plan line of `GET /api/workspaces/:id/pricing`, read for the close warning. */
interface PricingSummary {
  planName: string | null;
  totalCents: number | null;
}

/**
 * Who can be offered as the new owner: an ACTIVE member who is not already the
 * owner. The API (`POST /:id/transfer-ownership`) refuses everyone else, so the
 * picker never offers them.
 */
export function transferCandidates(members: WorkspaceMemberRow[]): WorkspaceMemberRow[] {
  return members.filter((m) => m.status === 'active' && m.role !== 'owner' && !!m.userId);
}

function memberLabel(m: WorkspaceMemberRow): string {
  const name = m.name?.trim();
  const email = m.email?.trim();
  const who = name && email ? `${name} (${email})` : name || email || m.userId;
  return `${who} · ${m.role}`;
}

/**
 * Central's step-up refusal (403/429) for close and transfer — see
 * CONTRACT-central-v2 "Step-up re-authentication". Never 401: 401 is terminal.
 */
interface StepUpRefusal {
  code: 'REAUTH_REQUIRED' | 'CURRENT_PASSWORD_INCORRECT' | 'TOO_MANY_PASSWORD_ATTEMPTS';
  retryAfter?: number;
}

const STEP_UP_CODES = new Set(['REAUTH_REQUIRED', 'CURRENT_PASSWORD_INCORRECT', 'TOO_MANY_PASSWORD_ATTEMPTS']);

export function readStepUpRefusal(body: unknown): StepUpRefusal | null {
  const code = (body as { code?: unknown } | null)?.code;
  if (typeof code !== 'string' || !STEP_UP_CODES.has(code)) return null;
  const retryAfter = (body as { retryAfter?: unknown }).retryAfter;
  return { code: code as StepUpRefusal['code'], ...(typeof retryAfter === 'number' ? { retryAfter } : {}) };
}

/** Where "Sign in again" goes: Central sign-in, returning to this exact page. */
export function reauthLoginHref(currentPath: string): string {
  return `/auth/login?continue=${encodeURIComponent(currentPath)}`;
}

function stepUpMessage(r: StepUpRefusal, hasPassword: boolean): string {
  switch (r.code) {
    case 'CURRENT_PASSWORD_INCORRECT':
      return 'That password is incorrect.';
    case 'TOO_MANY_PASSWORD_ATTEMPTS': {
      const minutes = r.retryAfter ? Math.max(1, Math.ceil(r.retryAfter / 60)) : null;
      return `Too many incorrect passwords.${minutes ? ` Try again in ${minutes} minute${minutes === 1 ? '' : 's'},` : ''} or sign in again to confirm.`;
    }
    default:
      return hasPassword
        ? 'For your security, confirm it’s you: enter your password, or sign in again.'
        : 'For your security, sign in again to confirm it’s you, then retry.';
  }
}

/** What the close response's `subscriptionStatus` means, in words. */
function closeOutcomeMessage(subscriptionStatus: unknown): string {
  switch (subscriptionStatus) {
    case 'scheduled_cancel':
      return 'Workspace closed. Its subscription ends at the close of the current billing period.';
    case 'canceled':
      return 'Workspace closed. Its subscription was cancelled.';
    case 'unchanged':
      return 'Workspace closed, but its subscription could not be cancelled automatically. Cancel it from Billing.';
    default:
      return 'Workspace closed';
  }
}

/**
 * General settings tab
 *
 * Features:
 * - Workspace name editing
 * - Avatar upload/remove
 * - Workspace type display
 * - Workspace ID copy
 * - Close workspace / transfer ownership
 */
export function GeneralTab({ workspaceId, authToken }: GeneralTabProps) {
  const [workspace, setWorkspace] = useState<WorkspaceDetails | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [editName, setEditName] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [closeConfirmName, setCloseConfirmName] = useState('');
  const [closeError, setCloseError] = useState<string | null>(null);
  const [pricing, setPricing] = useState<PricingSummary | null>(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferCandidatesList, setTransferCandidatesList] = useState<WorkspaceMemberRow[] | null>(null);
  const [newOwnerUserId, setNewOwnerUserId] = useState('');
  const [transferError, setTransferError] = useState<string | null>(null);
  // Step-up re-authentication (server-enforced): password users confirm with
  // their password; everyone can instead sign in again.
  const [hasPassword, setHasPassword] = useState<boolean | null>(null);
  // The signed-in account's email: the hidden username a password manager pairs
  // with the re-auth password, so it never lands in the typed-name field.
  const [accountEmail, setAccountEmail] = useState('');
  const [reauthPassword, setReauthPassword] = useState('');
  const [stepUp, setStepUp] = useState<StepUpRefusal | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isOwner = userRole === 'owner';

  // Only the owner sees close/transfer, so only the owner needs to know which
  // proof of identity this account can give.
  useEffect(() => {
    if (!isOwner || hasPassword !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await authFetch('/api/users/me', authToken);
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled) return;
        setHasPassword(data?.user?.providers?.password === true);
        if (typeof data?.user?.email === 'string') setAccountEmail(data.user.email);
      } catch {
        // unknown: the server will say what it needs
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOwner, authToken, hasPassword]);

  function resetStepUp() {
    setReauthPassword('');
    setStepUp(null);
  }

  /** The step-up proof to send: the typed password, or nothing (a recent sign-in may suffice). */
  function stepUpBody(): { currentPassword?: string } {
    return reauthPassword ? { currentPassword: reauthPassword } : {};
  }

  const passwordRequired = hasPassword === true;
  const stepUpReady = !passwordRequired || reauthPassword.length > 0;

  useEffect(() => {
    loadWorkspace();
  }, [workspaceId]);

  async function loadWorkspace() {
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}`, authToken);
      if (response.ok) {
        const data = await response.json();
        setWorkspace(data.workspace);
        setEditName(data.workspace.name);
        if (data.membership?.role) {
          setUserRole(data.membership.role);
        }
      }
    } catch {
      toast.error('Failed to load workspace');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSaveName() {
    if (!editName.trim() || editName === workspace?.name) {
      setIsEditingName(false);
      return;
    }

    setIsSaving(true);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}`, authToken, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ name: editName.trim() }),
      });

      if (response.ok) {
        const data = await response.json();
        setWorkspace(data.workspace);
        setIsEditingName(false);
        toast.success('Workspace name updated');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update name');
      }
    } catch {
      toast.error('Failed to update name');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleAvatarUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a PNG, JPEG, or WebP image');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setIsUploading(true);
    try {
      // Step 1: Get presigned URL
      const presignRes = await authFetch('/api/assets/presign', authToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({
          assetType: 'workspace-avatar',
          entityId: workspaceId,
          mimeType: file.type,
          fileSize: file.size,
        }),
      });

      if (!presignRes.ok) {
        const error = await presignRes.json();
        throw new Error(error.error || 'Failed to get upload URL');
      }

      const { uploadUrl, assetKey, version } = await presignRes.json();

      // Step 2: Upload to S3 (direct S3 URL, no Central auth)
      const uploadRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      if (!uploadRes.ok) {
        throw new Error('Failed to upload image');
      }

      // Step 3: Commit the upload
      const commitRes = await authFetch('/api/assets/commit', authToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({
          assetType: 'workspace-avatar',
          entityId: workspaceId,
          assetKey,
          version,
        }),
      });

      if (!commitRes.ok) {
        throw new Error('Failed to save avatar');
      }

      const { cdnUrl } = await commitRes.json();
      setWorkspace((prev) => (prev ? { ...prev, avatarUrl: cdnUrl } : null));
      toast.success('Avatar updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload avatar');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }

  async function handleRemoveAvatar() {
    // Note: This would typically call a DELETE endpoint
    // For now, we'll update with null avatar
    setIsSaving(true);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}`, authToken, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ avatarUrl: null }),
      });

      if (response.ok) {
        setWorkspace((prev) => (prev ? { ...prev, avatarUrl: null } : null));
        toast.success('Avatar removed');
      }
    } catch {
      toast.error('Failed to remove avatar');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCopyId() {
    navigator.clipboard.writeText(workspaceId);
    toast.success('Workspace ID copied');
  }

  async function handlePurposeChange(newPurpose: string) {
    if (newPurpose === workspace?.purpose) return;

    setIsSaving(true);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}`, authToken, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ purpose: newPurpose }),
      });

      if (response.ok) {
        const data = await response.json();
        setWorkspace((prev) => prev ? { ...prev, purpose: data.workspace.purpose } : null);
        toast.success('Workspace purpose updated');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update purpose');
      }
    } catch {
      toast.error('Failed to update purpose');
    } finally {
      setIsSaving(false);
    }
  }

  /**
   * Open the close dialog and read the plan for its subscription warning.
   * The warning is best-effort: a pricing read that fails leaves the generic
   * wording, never blocks the dialog.
   */
  async function openCloseDialog() {
    setCloseConfirmName('');
    setCloseError(null);
    resetStepUp();
    setPricing(null);
    setShowCloseConfirm(true);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/pricing`, authToken);
      if (!response.ok) return;
      const data = await response.json();
      const p = data?.pricing;
      if (p && typeof p === 'object') {
        setPricing({
          planName: typeof p.plan?.name === 'string' ? p.plan.name : null,
          totalCents: typeof p.totalCents === 'number' ? p.totalCents : null,
        });
      }
    } catch {
      // keep the generic warning
    }
  }

  function closeCloseDialog() {
    setShowCloseConfirm(false);
    setCloseConfirmName('');
    setCloseError(null);
    resetStepUp();
  }

  // The API compares `confirmName.trim()` with the stored name exactly.
  const closeNameMatches = !!workspace && closeConfirmName.trim() === workspace.name;

  async function handleCloseWorkspace() {
    if (!closeNameMatches || !stepUpReady) return;
    setIsSaving(true);
    setCloseError(null);
    setStepUp(null);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/close`, authToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmName: closeConfirmName.trim(), ...stepUpBody() }),
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        setWorkspace((prev) => (prev ? { ...prev, status: 'closed' } : null));
        closeCloseDialog();
        toast.success(closeOutcomeMessage(data?.subscriptionStatus));
      } else {
        const error = await response.json().catch(() => ({}));
        const refusal = readStepUpRefusal(error);
        if (refusal) {
          setStepUp(refusal);
          setReauthPassword('');
          return;
        }
        setCloseError(
          error?.code === 'CONFIRMATION_REQUIRED'
            ? 'The name you typed does not match this workspace’s name.'
            : error?.error || 'Failed to close workspace',
        );
      }
    } catch {
      setCloseError('Failed to close workspace');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleReopenWorkspace() {
    setIsSaving(true);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/reopen`, authToken, {
        method: 'POST',
        
      });

      if (response.ok) {
        setWorkspace((prev) => (prev ? { ...prev, status: 'active' } : null));
        toast.success('Workspace reopened');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to reopen workspace');
      }
    } catch {
      toast.error('Failed to reopen workspace');
    } finally {
      setIsSaving(false);
    }
  }

  /** Open the transfer dialog and load the members who can become owner. */
  async function openTransferDialog() {
    setNewOwnerUserId('');
    setTransferError(null);
    resetStepUp();
    setTransferCandidatesList(null);
    setShowTransferModal(true);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/members`, authToken);
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        setTransferError(error?.error || 'Could not load members');
        setTransferCandidatesList([]);
        return;
      }
      const data = await response.json();
      setTransferCandidatesList(transferCandidates(Array.isArray(data?.members) ? data.members : []));
    } catch {
      setTransferError('Could not load members');
      setTransferCandidatesList([]);
    }
  }

  function closeTransferDialog() {
    setShowTransferModal(false);
    setNewOwnerUserId('');
    setTransferError(null);
    resetStepUp();
  }

  async function handleTransferOwnership() {
    if (!newOwnerUserId || !stepUpReady) return;

    setIsSaving(true);
    setTransferError(null);
    setStepUp(null);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/transfer-ownership`, authToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newOwnerUserId, ...stepUpBody() }),
      });

      if (response.ok) {
        closeTransferDialog();
        toast.success('Ownership transferred. You are now an admin of this workspace.');
        // Reload to reflect the caller's new (admin) role.
        loadWorkspace();
      } else {
        const error = await response.json().catch(() => ({}));
        const refusal = readStepUpRefusal(error);
        if (refusal) {
          setStepUp(refusal);
          setReauthPassword('');
          return;
        }
        setTransferError(error?.error || 'Failed to transfer ownership');
      }
    } catch {
      setTransferError('Failed to transfer ownership');
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <div className={`${CARD} p-8 text-center`}>
        <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className={`${CARD} p-8 text-center`}>
        <p className="text-gray-500">Failed to load workspace</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Workspace profile */}
      <section className={CARD}>
        <div className="flex flex-col gap-4 border-b border-gray-100 p-5 sm:flex-row sm:items-center sm:p-6">
          <div className="flex h-16 w-16 flex-none items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-600/20 ring-1 ring-black/5 sm:h-20 sm:w-20">
            {workspace.avatarUrl ? (
              <img src={workspace.avatarUrl} alt={workspace.name} className="h-full w-full object-cover" />
            ) : (
              <span className="text-2xl font-semibold text-white sm:text-3xl">{workspace.name.charAt(0).toUpperCase()}</span>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500">Workspace name</p>
            {isEditingName ? (
              <div className="mt-1.5 flex flex-col gap-2 sm:flex-row sm:items-center">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className={`${INPUT} flex-1`}
                  placeholder="Workspace name"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button onClick={handleSaveName} disabled={isSaving} className={PRIMARY}>
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingName(false);
                      setEditName(workspace.name);
                    }}
                    className={SECONDARY}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-0.5 flex items-center gap-2">
                <h2 className="min-w-0 break-words text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">{workspace.name}</h2>
                <button
                  onClick={() => setIsEditingName(true)}
                  className="flex-none rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-emerald-50 hover:text-emerald-700"
                  aria-label="Edit workspace name"
                  title="Edit name"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.232-6.232a2.5 2.5 0 113.536 3.536L12.536 16.536 8 18l1.464-4.536z" />
                  </svg>
                </button>
              </div>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleAvatarUpload}
                className="hidden"
              />
              <button onClick={() => fileInputRef.current?.click()} disabled={isUploading} className={SECONDARY}>
                {isUploading ? 'Uploading...' : workspace.avatarUrl ? 'Change logo' : 'Upload logo'}
              </button>
              {workspace.avatarUrl && (
                <button
                  onClick={handleRemoveAvatar}
                  disabled={isSaving}
                  className="rounded-xl px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                >
                  Remove
                </button>
              )}
              <span className="text-xs text-gray-400">PNG, JPEG, or WebP. Max 5MB.</span>
            </div>
          </div>
        </div>

        <dl className="divide-y divide-gray-100">
          <Row label="Workspace type">
            <span className={CHIP}>{TYPE_LABELS[workspace.type] ?? workspace.type}</span>
          </Row>
          <Row label="Workspace purpose">
            {userRole === 'owner' ? (
              <select
                value={workspace.purpose ?? 'business'}
                onChange={(e) => handlePurposeChange(e.target.value)}
                disabled={isSaving}
                className={`${INPUT} w-full sm:w-56`}
              >
                <option value="personal">Personal Life</option>
                <option value="business">Business</option>
                <option value="client">Client Services</option>
              </select>
            ) : (
              <span className={CHIP}>{PURPOSE_LABELS[workspace.purpose] ?? workspace.purpose}</span>
            )}
          </Row>
          <Row label="Workspace ID">
            <div className="flex w-full min-w-0 items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-600 sm:text-sm">
                {workspaceId}
              </code>
              <button onClick={handleCopyId} className={SECONDARY}>
                Copy
              </button>
            </div>
          </Row>
        </dl>
      </section>

      {/* Workspace information (read-only) */}
      <section className={CARD}>
        <div className="border-b border-gray-100 px-5 py-4 sm:px-6">
          <h2 className="text-base font-semibold text-gray-900">About this workspace</h2>
          <p className="mt-0.5 text-sm text-gray-500">System details about this workspace</p>
        </div>
        <dl className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-3 sm:p-6">
          <Tile label="Status">
            <span className="inline-flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${workspace.status === 'active' ? 'bg-emerald-500' : 'bg-gray-400'}`} />
              {workspace.status === 'active' ? 'Active' : 'Closed'}
            </span>
          </Tile>
          <Tile label="Created">{formatDate(workspace.createdAt)}</Tile>
          <Tile label="Configuration">
            {workspace.onboardingVersion === 2 ? 'v2 (Focus-based)' : 'v1 (Legacy)'}
          </Tile>
        </dl>
      </section>

      {/* Danger zone — owner only: Central refuses close, reopen and transfer for everyone else. */}
      {isOwner ? (
        <section className={`${CARD} ring-red-100`} data-testid="danger-zone">
          <div className="border-b border-red-100 px-5 py-4 sm:px-6">
            <h2 className="text-base font-semibold text-red-700">Danger zone</h2>
            <p className="mt-0.5 text-sm text-gray-500">These change who owns this workspace or whether it stays open.</p>
          </div>
          <div className="divide-y divide-gray-100">
            {workspace.status !== 'closed' && (
              <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Transfer ownership</h3>
                  <p className="text-sm text-gray-500">Make another active member the owner. You become an admin.</p>
                </div>
                <button
                  onClick={openTransferDialog}
                  className="flex-none rounded-xl border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50"
                >
                  Transfer
                </button>
              </div>
            )}

            <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  {workspace.status === 'closed' ? 'Reopen workspace' : 'Close workspace'}
                </h3>
                <p className="text-sm text-gray-500">
                  {workspace.status === 'closed'
                    ? 'Reactivate this workspace and restore access'
                    : 'Deactivate this workspace and cancel its subscription'}
                </p>
              </div>
              {workspace.status === 'closed' ? (
                <button onClick={handleReopenWorkspace} disabled={isSaving} className={`${PRIMARY} flex-none`}>
                  {isSaving ? 'Reopening...' : 'Reopen'}
                </button>
              ) : (
                <button
                  onClick={openCloseDialog}
                  className="flex-none rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-red-700"
                >
                  Close workspace
                </button>
              )}
            </div>
          </div>
        </section>
      ) : (
        <p className="px-1 text-sm text-gray-500" data-testid="danger-zone-owner-only">
          Only the workspace owner can transfer ownership or close this workspace.
        </p>
      )}

      {/* Close confirmation: typed workspace name + subscription warning. */}
      {showCloseConfirm && (
        <Dialog title="Close workspace?">
          <p className="mb-3 text-gray-600">
            Closing deactivates <strong>{workspace.name}</strong>. Every member loses access, and no app can sign in to it
            until the owner reopens it.
          </p>
          <div className="mb-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200" data-testid="close-subscription-warning">
            {pricing?.planName && pricing.totalCents !== null && pricing.totalCents > 0 ? (
              <>
                This workspace is on the <strong>{pricing.planName}</strong> plan. Its subscription is cancelled and ends at
                the close of the current billing period; it will not renew.
              </>
            ) : pricing?.planName ? (
              <>
                This workspace is on the <strong>{pricing.planName}</strong> plan. Any paid subscription is cancelled at the
                end of the current billing period.
              </>
            ) : (
              <>Any paid subscription is cancelled at the end of the current billing period; it will not renew.</>
            )}
          </div>
          <label htmlFor="close-confirm-name" className="mb-1 block text-sm font-medium text-gray-700">
            Type <strong>{workspace.name}</strong> to confirm
          </label>
          {/* Its own form, and nothing that reads as a username: Chrome otherwise
              pairs the next text field before a password field with the saved
              login and fills the account email here. */}
          <form autoComplete="off" onSubmit={(e) => e.preventDefault()} data-testid="close-confirm-form">
            <input
              id="close-confirm-name"
              name="workspace-close-confirmation"
              type="text"
              value={closeConfirmName}
              onChange={(e) => setCloseConfirmName(e.target.value)}
              {...NOT_A_CREDENTIAL}
              className={`${INPUT} mb-3 w-full`}
            />
          </form>
          <StepUpFields
            idPrefix="close"
            hasPassword={hasPassword}
            accountEmail={accountEmail}
            password={reauthPassword}
            onPassword={setReauthPassword}
            refusal={stepUp}
          />
          {closeError && (
            <p role="alert" className="mb-3 text-sm text-red-600">
              {closeError}
            </p>
          )}
          <div className="flex justify-end gap-3">
            <button onClick={closeCloseDialog} className={SECONDARY}>
              Cancel
            </button>
            <button
              onClick={handleCloseWorkspace}
              disabled={isSaving || !closeNameMatches || !stepUpReady}
              className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {isSaving ? 'Closing...' : 'Close workspace'}
            </button>
          </div>
        </Dialog>
      )}

      {/* Transfer ownership: pick an active member; the API takes their user id. */}
      {showTransferModal && (
        <Dialog title="Transfer ownership">
          <p className="mb-4 text-gray-600">
            The new owner gets full control of this workspace, including billing and closing it. You stay on as an admin.
          </p>
          {transferCandidatesList === null ? (
            <p className="mb-4 text-sm text-gray-500">Loading members…</p>
          ) : transferCandidatesList.length === 0 ? (
            <p className="mb-4 text-sm text-gray-600" data-testid="transfer-no-candidates">
              There is no other active member to transfer to. Invite someone from Members first.
            </p>
          ) : (
            <>
              <label htmlFor="transfer-new-owner" className="mb-1 block text-sm font-medium text-gray-700">
                New owner
              </label>
              <select
                id="transfer-new-owner"
                name="transfer-new-owner"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                value={newOwnerUserId}
                onChange={(e) => setNewOwnerUserId(e.target.value)}
                className={`${INPUT} mb-4 w-full`}
              >
                <option value="">Choose a member…</option>
                {transferCandidatesList.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {memberLabel(m)}
                  </option>
                ))}
              </select>
            </>
          )}
          {transferCandidatesList !== null && transferCandidatesList.length > 0 && (
            <StepUpFields
              idPrefix="transfer"
              hasPassword={hasPassword}
              accountEmail={accountEmail}
              password={reauthPassword}
              onPassword={setReauthPassword}
              refusal={stepUp}
            />
          )}
          {transferError && (
            <p role="alert" className="mb-3 text-sm text-red-600">
              {transferError}
            </p>
          )}
          <div className="flex justify-end gap-3">
            <button onClick={closeTransferDialog} className={SECONDARY}>
              Cancel
            </button>
            <button
              onClick={handleTransferOwnership}
              disabled={isSaving || !newOwnerUserId || !stepUpReady}
              className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {isSaving ? 'Transferring...' : 'Transfer ownership'}
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

const CARD = 'overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-900/5';
/** Attributes that keep browsers and password managers from treating a field as a login field. */
const NOT_A_CREDENTIAL = {
  autoComplete: 'off',
  autoCorrect: 'off',
  autoCapitalize: 'off',
  spellCheck: false,
  'data-lpignore': 'true',
  'data-1p-ignore': 'true',
  'data-bwignore': 'true',
  'data-form-type': 'other',
} as const;
const CHIP = 'inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-800 ring-1 ring-emerald-100';
const INPUT =
  'rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 disabled:opacity-50';
const PRIMARY =
  'rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-emerald-600/25 transition-colors hover:bg-emerald-700 disabled:opacity-50';
const SECONDARY =
  'flex-none rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-center sm:gap-6 sm:px-6">
      <dt className="text-sm font-medium text-gray-500 sm:w-44 sm:flex-none">{label}</dt>
      <dd className="flex min-w-0 flex-1 items-center">{children}</dd>
    </div>
  );
}

function Tile({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-gray-50 p-4 ring-1 ring-gray-100">
      <dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">{label}</dt>
      <dd className="mt-1.5 text-sm font-semibold text-gray-900">{children}</dd>
    </div>
  );
}

/**
 * The re-authentication part of the close/transfer dialogs. Central enforces
 * it (403 REAUTH_REQUIRED / CURRENT_PASSWORD_INCORRECT, 429
 * TOO_MANY_PASSWORD_ATTEMPTS); this only collects the proof and explains a
 * refusal. "Sign in again" works for every account, including Google-only.
 */
function StepUpFields({
  idPrefix,
  hasPassword,
  accountEmail,
  password,
  onPassword,
  refusal,
}: {
  idPrefix: string;
  hasPassword: boolean | null;
  accountEmail: string;
  password: string;
  onPassword: (v: string) => void;
  refusal: StepUpRefusal | null;
}) {
  const loginHref = reauthLoginHref(
    typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/',
  );
  return (
    <div className="mb-3" data-testid={`${idPrefix}-step-up`}>
      {hasPassword === true && (
        // A credential form of its own: the hidden username (the signed-in
        // account) is what a password manager pairs the saved login with, so it
        // fills this password field and nothing else in the dialog.
        <form onSubmit={(e) => e.preventDefault()} data-testid={`${idPrefix}-reauth-form`}>
          <input
            type="text"
            name="username"
            autoComplete="username"
            value={accountEmail}
            readOnly
            hidden
            aria-hidden="true"
            tabIndex={-1}
          />
          <label htmlFor={`${idPrefix}-reauth-password`} className="mb-1 block text-sm font-medium text-gray-700">
            Confirm with your password
          </label>
          <input
            id={`${idPrefix}-reauth-password`}
            name="current-password"
            type="password"
            value={password}
            onChange={(e) => onPassword(e.target.value)}
            autoComplete="current-password"
            className={`${INPUT} mb-2 w-full`}
          />
        </form>
      )}
      {hasPassword !== true && !refusal && (
        <p className="mb-2 text-xs text-gray-500">
          If you haven’t signed in recently, you’ll be asked to sign in again to confirm.
        </p>
      )}
      {refusal && (
        <p role="alert" className="mb-2 text-sm text-red-600" data-testid={`${idPrefix}-step-up-refusal`}>
          {stepUpMessage(refusal, hasPassword === true)}
        </p>
      )}
      <a href={loginHref} className="text-sm font-medium text-emerald-700 hover:underline">
        Sign in again to confirm
      </a>
    </div>
  );
}

function Dialog({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div role="dialog" aria-modal="true" aria-label={title} className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-black/5">
        <h3 className="mb-2 text-lg font-semibold text-gray-900">{title}</h3>
        {children}
      </div>
    </div>
  );
}
